import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Settings2, Save, Trash2, Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { DashboardConfig, WidgetLayout } from "@/hooks/useDashboardConfig";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface DashboardConfigDialogProps {
  configs: DashboardConfig[];
  currentConfig: DashboardConfig | null;
  onSave: (name: string, layout: WidgetLayout[], visibleWidgets: string[], isDefault: boolean) => void;
  onDelete: (configId: string) => void;
  onSetDefault: (configId: string) => void;
  onSelectConfig: (config: DashboardConfig) => void;
  visibleWidgets: string[];
  onToggleWidget: (widgetId: string) => void;
}

const AVAILABLE_WIDGETS = [
  { id: "active_orders", label: "Órdenes Activas" },
  { id: "in_progress_processes", label: "Procesos en Curso" },
  { id: "active_operators", label: "Operarios Trabajando" },
  { id: "completion_rate", label: "Tasa de Finalización" },
  { id: "alerts", label: "Alertas" },
  { id: "realtime_chart", label: "Gráfico en Tiempo Real" },
  { id: "performance_chart", label: "Gráfico de Rendimiento" },
  { id: "personal_stats", label: "Estadísticas Personal" },
  { id: "referencias_stats", label: "Referencias Proyectos" },
  { id: "inventario_general", label: "Inventario General" },
  { id: "eventos_proximos", label: "Eventos Próximos" },
];

export const DashboardConfigDialog = ({
  configs,
  currentConfig,
  onSave,
  onDelete,
  onSetDefault,
  onSelectConfig,
  visibleWidgets,
  onToggleWidget,
}: DashboardConfigDialogProps) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  const handleSave = () => {
    if (!newViewName.trim() || !currentConfig) return;
    
    onSave(
      newViewName,
      currentConfig.layout,
      visibleWidgets,
      saveAsDefault
    );
    
    setNewViewName("");
    setSaveAsDefault(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings2 className="w-4 h-4" />
          {t.dashboard?.configure || "Configurar"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t.dashboard?.dashboardSettings || "Configuración del Dashboard"}</DialogTitle>
          <DialogDescription>
            {t.dashboard?.dashboardSettingsDesc || "Personaliza tu dashboard, elige widgets y guarda vistas"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Select existing view */}
          <div>
            <Label>{t.dashboard?.selectView || "Seleccionar Vista"}</Label>
            <Select
              value={currentConfig?.id}
              onValueChange={(value) => {
                const config = configs.find((c) => c.id === value);
                if (config) onSelectConfig(config);
              }}
            >
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {configs.map((config) => (
                  <SelectItem key={config.id} value={config.id}>
                    {config.name}
                    {config.is_default && " ⭐"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Manage views */}
          {currentConfig && configs.length > 0 && (
            <div className="space-y-2">
              <Label>{t.dashboard?.manageViews || "Gestionar Vistas"}</Label>
              <div className="space-y-2">
                {configs.map((config) => (
                  <div key={config.id} className="flex items-center justify-between p-2 border border-border rounded-lg">
                    <span className="font-medium">
                      {config.name}
                      {config.is_default && " ⭐"}
                    </span>
                    <div className="flex gap-2">
                      {!config.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onSetDefault(config.id)}
                          title={t.dashboard?.setAsDefault || "Establecer por defecto"}
                        >
                          <Star className="w-4 h-4" />
                        </Button>
                      )}
                      {configs.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(config.id)}
                          title={t.common?.delete || "Eliminar"}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Widget selection */}
          <div>
            <Label>{t.dashboard?.selectWidgets || "Seleccionar Widgets"}</Label>
            <div className="mt-2 space-y-2">
              {AVAILABLE_WIDGETS.map((widget) => (
                <div key={widget.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={widget.id}
                    checked={visibleWidgets.includes(widget.id)}
                    onCheckedChange={() => onToggleWidget(widget.id)}
                  />
                  <label
                    htmlFor={widget.id}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {widget.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Save new view */}
          <div className="space-y-4 pt-4 border-t border-border">
            <Label>{t.dashboard?.saveNewView || "Guardar como Nueva Vista"}</Label>
            <Input
              placeholder={t.dashboard?.viewName || "Nombre de la vista"}
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
            />
            <div className="flex items-center space-x-2">
              <Checkbox
                id="saveAsDefault"
                checked={saveAsDefault}
                onCheckedChange={(checked) => setSaveAsDefault(checked as boolean)}
              />
              <label
                htmlFor="saveAsDefault"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t.dashboard?.setAsDefault || "Establecer como predeterminada"}
              </label>
            </div>
            <Button onClick={handleSave} disabled={!newViewName.trim()} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              {t.dashboard?.saveView || "Guardar Vista"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
